/* ---------------------------------------------------------------------
 *                           Import Packages
 * --------------------------------------------------------------------- */

// React
import React, { useState, useEffect } from "react";

// Components
import ChatInput from "./ChatInput";
import ChatContainer from "./ChatContainer";

// MUI
import { Box, Paper, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Utils
import { fakeChatData } from "../../lib/utils/fakeData";
import { getChatStream } from "../../lib/service/userChat";
import { getChatData, setChatData } from "../../lib/data/chatData";

/* ---------------------------------------------------------------------
 *                             Chat Component
 * --------------------------------------------------------------------- */

/**
 * 定义AI对话组件
 * @param {boolean} isChatOpen - 控制AI对话窗口的打开和关闭
 * @param {function} toggleChatOpen - 切换AI对话窗口的打开和关闭状态
 * @function Chat
 * @returns AI对话组件
 */
export default function Chat({ toggleChatOpen }) {
  /**
   * 聊天数据
   * @type {Array}
   */
  const [messages, setMessages] = useState(() => {
    const savedMessages = getChatData();
    return savedMessages || fakeChatData;
  });

  /**
   * 存储用户输入消息
   * @type {string}
   */
  const [message, setMessage] = useState("");

  /**
   * 控制AI对话的加载状态
   * @type {boolean}
   */
  const [isLoading, setIsLoading] = useState(false);

  // 当消息更新时保存到本地存储
  useEffect(() => {
    setChatData(messages);
  }, [messages]);

  /**
   * 处理用户输入消息
   * @param {object} e 事件对象
   * @return {void}
   */
  const handleInputChange = (e) => {
    setMessage(e.target.value);
  };

  /**
   * 处理发送消息
   * @return {void}
   */
  const handleSendMessage = async () => {
    if (message.trim() === "" || isLoading) return;

    try {
      setIsLoading(true);
      
      // 添加用户消息
      const userMessage = {
        id: messages.length + 1,
        sender: "user",
        content: message
      };
      
      setMessages(prev => [...prev, userMessage]);
      setMessage("");

      // 调用API获取AI回复
      const response = await getChatStream({ message });
      
      if (response.success) {
        // 添加AI回复
        const aiMessage = {
          id: messages.length + 2,
          sender: "ai",
          content: response.data
        };
        setMessages(prev => [...prev, aiMessage]);
      } else {
        // 处理错误情况
        const errorMessage = {
          id: messages.length + 2,
          sender: "ai",
          content: "抱歉，我遇到了一些问题，请稍后再试。"
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage = {
        id: messages.length + 2,
        sender: "ai",
        content: "抱歉，发生了错误，请稍后再试。"
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------------------------------------------------------------
   *                            Render Component
   * --------------------------------------------------------------------- */

  return (
    <Paper
      elevation={1}
      sx={{
        width: "24%",
        height: "100%",
        overflow: "auto",
        border: "1px solid #eee",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
        transition: "all 0.3s",
      }}
    >
      {/* 收起按钮 */}
      <Box sx={{ position: "absolute", top: 5, right: 9 }}>
        <IconButton onClick={toggleChatOpen}>
          <ExpandMoreIcon sx={{ fontSize: 35 }} />
        </IconButton>
      </Box>

      {/* 对话区 */}
      <ChatContainer chatMessages={messages} isLoading={isLoading} />

      {/* 输入框 */}
      <ChatInput
        message={message}
        onMessageInput={handleInputChange}
        onSendMessage={handleSendMessage}
      />
    </Paper>
  );
}
